import type { AuthUser, PrivilegeId } from '@stayon/auth-core';
import { PRIVILEGES } from '@stayon/auth-core';
import { can, canAccess, ROLE_GROUPS, type Role } from '@/shared/auth';

export interface TabItem {
  to: string;
  label: string;
  /** Erlaubte Rollen; ohne Angabe für alle Angemeldeten sichtbar. Spiegelt die Route-Guards. */
  roles?: Role[];
  /** Privilege-gesteuerte Reiter verwenden keine Rollen-Alternative. */
  privilege?: PrivilegeId;
}

const { staff: STAFF, operationsOwner: OPERATIONS_OWNER, staffOwner: STAFF_OWNER } = ROLE_GROUPS;

export const APARTMENT_TABS: TabItem[] = [
  { to: '/properties/$id', label: 'Übersicht', roles: OPERATIONS_OWNER },
  { to: '/properties/$id/stammdaten', label: 'Stammdaten', roles: STAFF },
  { to: '/properties/$id/bilder', label: 'Bilder', roles: STAFF },
  { to: '/properties/$id/channels', label: 'Kanäle', roles: STAFF },
  {
    to: '/properties/$id/preise',
    label: 'Preise & Belegung',
    privilege: PRIVILEGES.availabilityDistributionView,
  },
  { to: '/properties/$id/reinigung', label: 'Reinigung', roles: STAFF },
  { to: '/properties/$id/akte', label: 'Akte', roles: STAFF },
  { to: '/properties/$id/parking', label: 'Parkplätze', roles: STAFF },
  // Hand-off aus C4: die Seite heißt seit Block C „Ergebnis und Auslastung", der Reiter hieß
  // weiter „Finance". Und der Protokoll-Reiter zeigt keinen Änderungs-Log, sondern den Austausch
  // mit dem Kanalmanager.
  { to: '/properties/$id/finance', label: 'Auswertung', roles: STAFF_OWNER },
  { to: '/properties/$id/audit', label: 'Protokoll', roles: STAFF },
];

/** Im Archiv bleiben nur die lesenden Bereiche offen. */
const ARCHIVE_TABS = ['/properties/$id', '/properties/$id/finance', '/properties/$id/audit'];

export function visibleApartmentTabs(user: AuthUser | null, readOnly: boolean): TabItem[] {
  const visibleTabs = APARTMENT_TABS.filter((tab) =>
    tab.privilege ? can(user, tab.privilege) : canAccess(user, tab.roles),
  );
  return readOnly ? visibleTabs.filter((tab) => ARCHIVE_TABS.includes(tab.to)) : visibleTabs;
}

/**
 * Welcher Reiter zum aktuellen Pfad gehört. Die Übersicht ist der Sonderfall: ihr Pfad ist das
 * Präfix aller anderen, sie zählt deshalb nur bei exakter Übereinstimmung.
 */
export function activeApartmentTab(
  tabs: TabItem[],
  propertyId: string,
  currentPath: string,
): TabItem | undefined {
  return tabs.find((tab) => {
    const path = tab.to.replace('$id', propertyId);
    if (tab.to === '/properties/$id') {
      return currentPath === path || currentPath === `${path}/`;
    }
    return currentPath.startsWith(path);
  });
}
